import * as Sentry from '@sentry/react';

/**
 * Configuration Sentry pour le tracking d'erreurs
 *
 * Pour activer Sentry en production :
 * 1. Créer un projet sur https://sentry.io
 * 2. Ajouter VITE_SENTRY_DSN dans les variables d'environnement
 */

const SENTRY_DSN = import.meta.env.VITE_SENTRY_DSN;
const IS_PRODUCTION = import.meta.env.PROD;
const APP_VERSION = import.meta.env.VITE_APP_VERSION || '1.0.0';

/**
 * Initialiser Sentry
 * Ne s'initialise que si le DSN est configuré
 */
export function initSentry(): void {
  if (!SENTRY_DSN) {
    if (IS_PRODUCTION) {
      console.warn(
        '[Sentry] DSN non configuré. Ajoutez VITE_SENTRY_DSN pour activer le tracking d\'erreurs.'
      );
    }
    return;
  }

  Sentry.init({
    dsn: SENTRY_DSN,
    environment: IS_PRODUCTION ? 'production' : 'development',
    release: `stockchef@${APP_VERSION}`,

    // Performance Monitoring
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        // Masquer les données sensibles dans les replays
        maskAllText: false,
        maskAllInputs: true,
        blockAllMedia: false,
      }),
    ],

    // Sampling rates
    tracesSampleRate: IS_PRODUCTION ? 0.1 : 1.0, // 10% en prod, 100% en dev
    replaysSessionSampleRate: IS_PRODUCTION ? 0.1 : 0, // 10% des sessions en prod
    replaysOnErrorSampleRate: 1.0, // 100% des sessions avec erreur

    // Filtrer les erreurs non pertinentes
    beforeSend(event, hint) {
      const error = hint.originalException;

      // Ignorer certaines erreurs connues non critiques
      if (error instanceof Error) {
        // Erreurs de réseau (gérées par le mode offline)
        if (
          error.message.includes('Failed to fetch') ||
          error.message.includes('NetworkError') ||
          error.message.includes('Network request failed')
        ) {
          return null;
        }

        // Erreurs d'authentification Supabase (gérées par le flow auth)
        if (
          error.message.includes('JWT expired') ||
          error.message.includes('Invalid Refresh Token')
        ) {
          return null;
        }

        // Erreurs de permission (normales pour les utilisateurs non connectés)
        if (error.message.includes('permission denied')) {
          return null;
        }
      }

      return event;
    },

    // Tags par défaut
    initialScope: {
      tags: {
        app: 'stockchef',
        platform: 'web',
      },
    },
  });
}

/**
 * Capturer une erreur manuellement
 */
export function captureError(
  error: Error,
  context?: Record<string, unknown>
): string {
  return Sentry.captureException(error, {
    extra: context,
  });
}

/**
 * Capturer un message (pour les événements non-erreurs)
 */
export function captureMessage(
  message: string,
  level: 'info' | 'warning' | 'error' = 'info'
): string {
  return Sentry.captureMessage(message, level);
}

/**
 * Définir l'utilisateur courant pour le tracking
 */
export function setUser(user: { id: string; email?: string; username?: string } | null): void {
  if (user) {
    Sentry.setUser({
      id: user.id,
      email: user.email,
      username: user.username,
    });
  } else {
    Sentry.setUser(null);
  }
}

/**
 * Ajouter des tags pour un contexte spécifique
 */
export function setTag(key: string, value: string): void {
  Sentry.setTag(key, value);
}

/**
 * Ajouter du contexte supplémentaire
 */
export function setContext(name: string, context: Record<string, unknown>): void {
  Sentry.setContext(name, context);
}

/**
 * Démarrer une transaction pour le performance monitoring
 */
export function startTransaction(name: string, op: string): Sentry.Span | undefined {
  return Sentry.startInactiveSpan({ name, op });
}

// Export du module Sentry pour usage avancé
export { Sentry };
